/**
 * Bausteine der Oberflaeche.
 *
 * Bewusst schlicht gehalten: Die Formularfelder nehmen den Feldfehler des
 * Servers direkt entgegen, damit eine serverseitige Pruefung ohne
 * Zusatzaufwand am richtigen Feld erscheint.
 */

import { useState, type ChangeEvent, type ReactNode } from 'react';

import {
  formatAmount,
  formatPrice,
  formatQuantity,
  parseAmount,
  parsePrice,
  parseQuantity,
} from '@erp/shared';

// --- Feldrahmen --------------------------------------------------------------

type FieldProps = {
  label: string;
  htmlFor?: string;
  required?: boolean;
  error?: string;
  hint?: string;
  /** Breite im 12-spaltigen Raster des Formulars. */
  span?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
  children: ReactNode;
};

export function Field({ label, htmlFor, required, error, hint, span = 12, children }: FieldProps) {
  return (
    <div className={`field${span !== 12 ? ` field-${span}` : ''}`}>
      <label className={`field-label${required ? ' field-required' : ''}`} htmlFor={htmlFor}>
        {label}
      </label>
      {children}
      {error ? <span className="field-error">{error}</span> : null}
      {!error && hint ? <span className="field-hint">{hint}</span> : null}
    </div>
  );
}

// --- Texteingabe -------------------------------------------------------------

type TextInputProps = {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: 'text' | 'password' | 'email' | 'date' | 'month' | 'tel' | 'url';
  invalid?: boolean;
  disabled?: boolean;
  autoFocus?: boolean;
  maxLength?: number;
  autoComplete?: string;
};

export function TextInput({
  id,
  value,
  onChange,
  placeholder,
  type = 'text',
  invalid,
  disabled,
  autoFocus,
  maxLength,
  // Browser-Vorschlaege aus frueheren Eingaben stoeren in einer Fachanwendung
  // mehr, als sie helfen. Anmeldefelder setzen ausdruecklich etwas anderes.
  autoComplete = 'off',
}: TextInputProps) {
  return (
    <input
      id={id}
      className={`input${invalid ? ' invalid' : ''}`}
      type={type}
      value={value}
      placeholder={placeholder}
      disabled={disabled}
      autoFocus={autoFocus}
      maxLength={maxLength}
      autoComplete={autoComplete}
      onChange={(event: ChangeEvent<HTMLInputElement>) => onChange(event.target.value)}
    />
  );
}

export function TextArea({
  id,
  value,
  onChange,
  rows = 3,
  disabled,
  placeholder,
}: {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  rows?: number;
  disabled?: boolean;
  placeholder?: string;
}) {
  return (
    <textarea
      id={id}
      autoComplete="off"
      className="input"
      rows={rows}
      value={value}
      disabled={disabled}
      placeholder={placeholder}
      onChange={(event) => onChange(event.target.value)}
    />
  );
}

// --- Auswahl -----------------------------------------------------------------

export function Select<T extends string>({
  id,
  value,
  onChange,
  options,
  disabled,
  invalid,
  placeholder,
}: {
  id?: string;
  value: T | '';
  onChange: (value: T) => void;
  options: { value: T; label: string }[];
  disabled?: boolean;
  invalid?: boolean;
  placeholder?: string;
}) {
  return (
    <select
      id={id}
      className={`select input${invalid ? ' invalid' : ''}`}
      value={value}
      disabled={disabled}
      onChange={(event) => onChange(event.target.value as T)}
    >
      {placeholder !== undefined ? <option value="">{placeholder}</option> : null}
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
}

export function Checkbox({
  id,
  checked,
  onChange,
  label,
  disabled,
}: {
  id?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  disabled?: boolean;
}) {
  return (
    <div className="checkbox-row">
      <input
        id={id}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
      />
      <label htmlFor={id}>{label}</label>
    </div>
  );
}

// --- Zahleneingabe -----------------------------------------------------------

/**
 * Eingabefeld fuer Festkommazahlen.
 *
 * Waehrend der Eingabe bleibt der Rohtext stehen - erst beim Verlassen des
 * Feldes wird er in die Zieleinheit umgerechnet und formatiert
 * zurueckgeschrieben. Ohne das laesst sich in einem Feld, das nach jedem
 * Tastendruck formatiert, kein Komma tippen.
 */
type NumericKind = 'amount' | 'price' | 'quantity';

const PARSERS: Record<NumericKind, (input: string) => number | null> = {
  amount: parseAmount,
  price: parsePrice,
  quantity: parseQuantity,
};

const FORMATTERS: Record<NumericKind, (value: number) => string> = {
  amount: formatAmount,
  price: formatPrice,
  quantity: formatQuantity,
};

export function NumericInput({
  id,
  kind,
  value,
  onChange,
  disabled,
  invalid,
  allowNegative = false,
}: {
  id?: string;
  kind: NumericKind;
  /** Wert in der jeweiligen Festkomma-Einheit. */
  value: number;
  onChange: (value: number) => void;
  disabled?: boolean;
  invalid?: boolean;
  allowNegative?: boolean;
}) {
  const formatted = FORMATTERS[kind](value);

  // `source` merkt sich, aus welchem Wert der angezeigte Text entstanden
  // ist. Aendert sich der Wert von aussen, wird der Entwurf verworfen -
  // das ersetzt einen useEffect zum Nachziehen.
  const [draft, setDraft] = useState({ text: formatted, source: formatted });
  if (draft.source !== formatted) setDraft({ text: formatted, source: formatted });

  return (
    <input
      id={id}
      className={`input numeric${invalid ? ' invalid' : ''}`}
      type="text"
      inputMode="decimal"
      autoComplete="off"
      value={draft.text}
      disabled={disabled}
      onChange={(event) => setDraft({ text: event.target.value, source: formatted })}
      onFocus={(event) => event.target.select()}
      onBlur={() => {
        const parsed = PARSERS[kind](draft.text);
        const next = parsed === null ? 0 : allowNegative ? parsed : Math.max(0, parsed);
        setDraft({ text: FORMATTERS[kind](next), source: FORMATTERS[kind](next) });
        onChange(next);
      }}
    />
  );
}

// --- Rueckmeldungen ----------------------------------------------------------

export function Alert({
  kind = 'info',
  children,
}: {
  kind?: 'info' | 'error' | 'success' | 'warning';
  children: ReactNode;
}) {
  return <div className={`alert alert-${kind}`}>{children}</div>;
}

export function Badge({
  kind = 'neutral',
  children,
}: {
  kind?: 'neutral' | 'success' | 'warning' | 'danger' | 'accent';
  children: ReactNode;
}) {
  return <span className={`badge${kind === 'neutral' ? '' : ` badge-${kind}`}`}>{children}</span>;
}

export function LoadingBlock({ text = 'Wird geladen …' }: { text?: string }) {
  return (
    <div className="loading-block">
      <span className="spinner" aria-hidden="true" />
      <span>{text}</span>
    </div>
  );
}

export function EmptyState({ title, hint }: { title: string; hint?: string }) {
  return (
    <div className="table-empty">
      <div>{title}</div>
      {hint ? <div className="faint" style={{ marginTop: 4, fontSize: 13 }}>{hint}</div> : null}
    </div>
  );
}

// --- Dialog ------------------------------------------------------------------

export function Dialog({
  title,
  wide,
  onClose,
  children,
  footer,
}: {
  title: string;
  wide?: boolean;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <div
      className="dialog-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className={`dialog${wide ? ' dialog-wide' : ''}`}>
        <div className="dialog-header">
          <h2>{title}</h2>
        </div>
        <div className="dialog-body">{children}</div>
        {footer ? <div className="dialog-footer">{footer}</div> : null}
      </div>
    </div>
  );
}

// --- Einklappbare Karten ------------------------------------------------------

/**
 * Auf-/zugeklappt je Karte, gemerkt im Browser des Arbeitsplatzes.
 * Die Kopfzeile der Karte bleibt immer sichtbar - eingeklappt ist nur der
 * Inhalt.
 */
export function useCollapsed(key: string): [boolean, () => void] {
  const storageKey = `canalis.einklappen.${key}`;
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(storageKey) === '1';
    } catch {
      return false;
    }
  });

  function toggle() {
    setCollapsed((current) => {
      try {
        localStorage.setItem(storageKey, current ? '0' : '1');
      } catch {
        // Ohne Speicher gilt der Zustand eben nur bis zum Neuladen.
      }
      return !current;
    });
  }

  return [collapsed, toggle];
}

/** Kartentitel mit Auf-/Zuklapp-Pfeil. */
export function CollapsibleTitle({
  title,
  collapsed,
  onToggle,
  subtitle,
}: {
  title: string;
  collapsed: boolean;
  onToggle: () => void;
  subtitle?: string;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      title={collapsed ? 'Aufklappen' : 'Einklappen'}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        background: 'none',
        border: 'none',
        padding: 0,
        cursor: 'pointer',
        color: 'inherit',
        font: 'inherit',
        textAlign: 'left',
      }}
    >
      <span aria-hidden="true" style={{ fontSize: 11, width: 12 }}>
        {collapsed ? '▶' : '▼'}
      </span>
      <span>
        <h2 style={{ margin: 0, fontSize: 16 }}>{title}</h2>
        {subtitle && !collapsed ? (
          <span className="faint" style={{ fontSize: 12.5 }}>
            {subtitle}
          </span>
        ) : null}
      </span>
    </button>
  );
}
